import React, { Component } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  Mail, CheckCircle2, AlertTriangle, MailX, ArrowLeft, MessageSquare,
} from "lucide-react";

import SEO from "../layout/SEO";
import {
  Card,
  Input,
  FormField,
  Button,
} from "../../design-system";

export default class NewsletterUnsubscribe extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      token: "",
      hasValidToken: null,
      submitting: false,
      unsubscribed: false,
    };
  }

  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    const token = urlParams.get("token");

    if (!token) {
      this.setState({
        hasValidToken: false,
        email: email ? decodeURIComponent(email) : "",
      });
      return;
    }

    this.setState({
      hasValidToken: true,
      token: decodeURIComponent(token),
      email: email ? decodeURIComponent(email) : "",
    });
  }

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    if (this.state.submitting || this.state.unsubscribed) return;

    if (!this.state.hasValidToken || !this.state.token) {
      Store.addNotification({
        title: "Accès non autorisé",
        message: "Utilise le lien de désabonnement fourni dans l'email.",
        type: "danger",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 5000 },
      });
      return;
    }

    const { email, token } = this.state;
    if (!email || !email.includes("@")) {
      Store.addNotification({
        title: "Email invalide",
        message: "Vérifie ton adresse email.",
        type: "warning",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 3000 },
      });
      return;
    }

    this.setState({ submitting: true });

    try {
      const response = await axios.post("/api/newsletters/unsubscribe", {
        email: email.trim(),
        token,
      });

      if (response.data.success) {
        this.setState({ unsubscribed: true, submitting: false });
      }
    } catch (error) {
      console.error("Newsletter unsubscribe error:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Une erreur est survenue",
        type: "danger",
        insert: "top",
        container: "top-right",
        dismiss: { duration: 5000 },
      });
      this.setState({ submitting: false });
    }
  };

  renderSuccess() {
    return (
      <Card className="p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-success-bg text-success flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
        </div>
        <h2 className="text-[28px] leading-[1.1] font-bold tracking-tight">
          Désabonnement <span className="font-serif italic font-normal">confirmé.</span>
        </h2>
        <p className="text-sm text-mute mt-3 leading-relaxed">
          Tu ne recevras plus d'emails de notre part. Si tu changes d'avis,
          tu peux te réabonner à tout moment depuis le site.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-6 text-[13px] font-medium text-text hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
          Retour à l'accueil
        </Link>
      </Card>
    );
  }

  renderInvalid() {
    return (
      <Card className="p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-warn-bg text-warn flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7" strokeWidth={1.6} />
        </div>
        <h2 className="text-[28px] leading-[1.1] font-bold tracking-tight">
          Lien <span className="font-serif italic font-normal">invalide.</span>
        </h2>
        <p className="text-sm text-mute mt-3 leading-relaxed">
          Pour des raisons de sécurité, le désabonnement passe par le lien
          fourni dans l'email de newsletter. Si tu l'as perdu, contacte-moi
          pour en obtenir un nouveau.
        </p>
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            as={Link}
            to="/"
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />}
          >
            Accueil
          </Button>
          <Button
            as={Link}
            to="/contact"
            size="sm"
            leftIcon={<MessageSquare className="w-3.5 h-3.5" strokeWidth={1.8} />}
          >
            Me contacter
          </Button>
        </div>
      </Card>
    );
  }

  renderForm() {
    const { email, submitting, hasValidToken } = this.state;
    return (
      <Card className="p-6">
        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-bg-2 text-mute flex items-center justify-center mx-auto mb-3">
            <MailX className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <h2 className="text-[24px] leading-[1.1] font-bold tracking-tight">
            Se désabonner
          </h2>
          <p className="text-sm text-mute mt-2 leading-relaxed">
            Confirme ton adresse pour ne plus recevoir la newsletter.
          </p>
        </div>

        <form onSubmit={this.handleSubmit} className="space-y-4">
          <FormField label="Adresse email">
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="ton@email.com"
              value={email}
              onChange={this.handleChange}
              isDisabled={submitting || !hasValidToken}
              readOnly={Boolean(email) && hasValidToken}
              leftIcon={<Mail className="w-4 h-4" strokeWidth={1.8} />}
              required
            />
          </FormField>

          <Button
            type="submit"
            fullWidth
            variant="danger"
            isLoading={submitting}
            isDisabled={!hasValidToken}
          >
            {submitting ? "Traitement…" : "Me désabonner"}
          </Button>
        </form>
      </Card>
    );
  }

  render() {
    const { unsubscribed, hasValidToken } = this.state;

    return (
      <>
        <SEO
          title="Désabonnement Newsletter"
          description="Désabonnez-vous de la newsletter."
          keywords="newsletter, désabonnement, email"
        />
        <div
          data-ds-theme="default"
          className="fixed inset-0 overflow-y-auto bg-bg text-text flex flex-col"
        >
          <main className="flex-1 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
              <div className="text-center mb-8">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mb-6 text-mute hover:text-text transition-colors"
                >
                  <img src="/img/logo.png" alt="Portfolio" className="w-10 h-10 object-contain" />
                  <span className="text-lg font-bold tracking-tight">
                    Portfolio<span className="font-serif italic font-normal align-baseline">.</span>
                  </span>
                </Link>
                <p className="ds-eyebrow">— Newsletter</p>
              </div>

              {unsubscribed
                ? this.renderSuccess()
                : hasValidToken === false
                  ? this.renderInvalid()
                  : this.renderForm()}
            </div>
          </main>

          <footer className="px-4 py-4 text-center text-[11px] font-mono uppercase tracking-wider text-mute-2">
            Portfolio · Ascencia
          </footer>
        </div>
      </>
    );
  }
}
